import { BoxBase, DialogBase, StackBase, Tile, TileContextProvider, TypographyBase } from '@fmc-digital/ui';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PaymentCard } from '../molecule/paymentCard/PaymentCard';
import {
    selectCurrentLoanAddress,
    selectEligibilityById,
    selectEligibleForNDByLoanNumberV2,
    selectIsLoanInProgress,
    selectLoanNumberSelected,
    selectNotificationRequest,
    selectOfferByLoan,
    selectPropertyCheckValueError,
    selectRefactorNBPCard,
    selectSelectedLoanNumber,
    selectTeaserOffer,
    selectUserInfo,
    selectClearedNDLoans
} from '@store/selectors/widgets.selectors';
import { EventType, NaturalDisasterConstants } from 'shared-ui-components';
import { logApiEvent, logUserEvent } from '@analytics/amplifyLogging';
import { useDispatch, useSelector } from 'react-redux';
import { NewLoanInProgressTiles } from './NewLoanInProgressTiles';
import { Container, useMediaQuery, useTheme } from '@mui/material';
import { EscrowTile } from '@utils/tiles.data';
import { NaturalDisasterModal } from '@fmc-digital/servicing';
import { get, isEmpty } from 'lodash';
import { NavigateOptions, useNavigate } from 'react-router-dom';
import { withHeaderFooter } from '../../HOCs';
import { useQueryClient } from '@tanstack/react-query';
import { auth, config } from '../../../config';
import { fetchAlertAsync } from '@store/actions/alerts.actions';
import { SiteMessages } from '../molecule/site-messages/SiteMessages';
import { TeaserOffer } from '../molecule/TeaserOffer/TeaserOffer';
import { allowedVWOActionsMapping, CodingConstants, accountStatusPopupMessages, accountStatusWithoutPopupMessages } from '@constants';
import Onboarding from '@containers/Onboarding';
import { activityEventUpdate } from 'fmc-digital-common';
import NBPoffer from './NBPoffer';
import { AccountTemplate, Identifier } from '@types';
import { useRequestForbearanceMutation } from '@hooks/useRequestForbearanceMutation';
import { clearLoanNDStatus } from '@store/actions/widgets.actions';
import OfferDisclosuresConstants from '@components_v2/data/offer-terms';
import { getFullAppFeatureFlagsV2 } from '@store/reducers/config.reducer';
import { TurboTaxModal } from '@components_v2/UI/atoms/turbo-tax-modal/TurboTaxModal';
import { selectActiveAccount } from '@store/selectors/accounts.selector';
import PaperlessEnrollment from '@components_v2/UI/molecule/PaperlessEnrollment/PaperlessEnrollment';
import TaxDocumentWidget from '@components_v2/UI/molecule/TaxDocumentWidget/TaxDocumentWidget';
import { useAutoPayQuery } from '@hooks/useAutoPayQuery';
import { usePaymentHistoryQuery } from '@hooks/usePaymentHistoryQuery';
import { enqueueSnackbar } from 'notistack';
import { useVWOContext } from '../../../context/VWOContext';
import { fetchStatementAsync } from '@store/actions/statements.action';
import { useSnackBarProps } from '@hooks/useSnackbarProps';
import { PaymentCardSkeleton } from '../atoms/skeletons';
import { HomeValueContainer } from '@containers/HomeValueContainer';
import AccountStatusPopup from './AccountStatusPopup';
import moment from 'moment';
import { v4 } from 'uuid';
import useLaunchpadNavigation from '@hooks/useLaunchpadNavigation';
import { usePropertyValueQuery } from '@hooks/usePropertyValueQuery';

const useTurboTax = (initialState?: boolean) => {
    const [isOpen, setOpen] = React.useState(initialState ?? false);

    const open = useCallback(() => {
        setOpen(true);
    }, []);
    const close = useCallback(() => {
        setOpen(false);
    }, []);
    const toggle = useCallback(() => {
        setOpen(prev => !prev);
    }, []);
    return { open, close, toggle, isOpen };
};

const Columned = ({ children }: any) => {
    return (
        <StackBase gap={{ xs: '1rem', sm: '2rem' }} sx={{ width: { xs: '100%', sm: '50%' } }}>
            {children}
        </StackBase>
    );
};

export const ACPTiles = withHeaderFooter(
    () => {
        const isMount = useRef(false);
        const { RecordVWOEvent, RecordVWOAttributes } = useVWOContext();
        const { isLoading } = useAutoPayQuery();
        usePaymentHistoryQuery();
        const navigate = useNavigate();
        const theme = useTheme();
        const isMobile = useMediaQuery(theme.breakpoints.only('xs'));
        // value for this boolean should be retrieved form the store once api send is complete
        const natStateModal = useSelector(selectEligibleForNDByLoanNumberV2);
        const [displayAccountStatusPopup, setDisplayAccountStatusPopup] = useState(false);
        const [accountStatusTemplate, setAccountStatusTemplate] = useState({});
        const flags = useSelector(getFullAppFeatureFlagsV2);
        const dispatch = useDispatch();
        const selectedLoanNumber = useSelector(selectSelectedLoanNumber);
        const currentAddress = useSelector(selectCurrentLoanAddress);
        const templates = useSelector(state => selectOfferByLoan(state, get(selectedLoanNumber, ['identifier'], '')));
        const portCode = useSelector(selectUserInfo).portCode;
        const tCPA = useSelector(selectUserInfo).tcpaConsentRequired;
        const isPaperLessEligible = useSelector(state => selectEligibilityById(state, CodingConstants.paperLess));
        const is1098Eligible = useSelector(state => selectEligibilityById(state, CodingConstants.taxDocumentWidget));
        const isEscrowEligible = useSelector(state => selectEligibilityById(state, CodingConstants.eligibleForEscrowComplete));
        const queryClient = useQueryClient();
        const siteMessageRequest = useSelector(selectNotificationRequest);
        const teaserOffer = useSelector(state => selectTeaserOffer(state, get(selectedLoanNumber, ['identifier'], '')));
        const originations: Identifier[] | undefined = useSelector(selectIsLoanInProgress);
        const snackProps = useSnackBarProps({ severity: 'error' });
        const loanNumber = useSelector(selectLoanNumberSelected);
        const activeAccount: AccountTemplate[] = useSelector(selectActiveAccount);
        const selectedAccount: AccountTemplate = activeAccount?.find(ele => ele?.accountNumber === loanNumber);
        const { toggle, isOpen } = useTurboTax(false);
        const isHomeValueEligible = useSelector(state => selectEligibilityById(state, CodingConstants.homeValueWidgetId));

        const clearedNDLoans = useSelector(selectClearedNDLoans);

        const [disclosures, setDisclosures] = useState<boolean | string>(false);
        const [showNDModal, setShowNDModal] = useState(natStateModal);
        const propertyValueQuery = usePropertyValueQuery(loanNumber);
        const { isSuccess, isError, isLoading: homeValueLoading, isFetching } = propertyValueQuery;
        const isHomeValueCOmplete = (isSuccess || isError) && !homeValueLoading && !isFetching;
        const hasPropertyValueError = useSelector(state => selectPropertyCheckValueError(state));

        //Getting NBPcards for deeplink navigation.
        const handleLaunchpadNavigation = useLaunchpadNavigation();
        const pendingNBPcards = useSelector(selectRefactorNBPCard);

        //navigate to deeplink if no NBP.
        useEffect(() => {
            if (isEmpty(pendingNBPcards) && (isEmpty(clearedNDLoans) || clearedNDLoans.includes(loanNumber))) {
                handleLaunchpadNavigation();
            }
        }, [loanNumber, pendingNBPcards, clearedNDLoans]);

        useEffect(() => {
            if (!isEmpty(portCode)) {
                RecordVWOAttributes({ portCode: portCode });
            }
            if (tCPA != null) {
                RecordVWOAttributes({ tCPA: tCPA });
            }
        }, [RecordVWOAttributes, portCode, tCPA]);

        useEffect(() => {
            if (selectedLoanNumber?.allowPaperlessStatements) {
                RecordVWOAttributes({ paperless: selectedLoanNumber?.allowPaperlessStatements });
            }
        }, [RecordVWOAttributes, selectedLoanNumber]);
        useEffect(() => {
            if (selectedLoanNumber?.propertyAddress) {
                const { city, state, zip } = selectedLoanNumber?.propertyAddress;
                RecordVWOAttributes({
                    propertyCity: city,
                    propertyState: state,
                    propertyZipCode: zip
                });
            }
            if (selectedLoanNumber?.loanType) {
                RecordVWOAttributes({
                    loanType: selectedLoanNumber?.loanType
                });
            }
        }, [selectedLoanNumber, selectedAccount, RecordVWOAttributes]);

        useEffect(() => {
            if (selectedAccount?.accountStatus?.name) {
                RecordVWOAttributes({
                    loanStatus: selectedAccount?.accountStatus?.name
                });
            }
            const statusTemplate = accountStatusPopupMessages.find(item => selectedAccount?.accountStatus?.nameKey === item.status);
            statusTemplate ? setDisplayAccountStatusPopup(true) : setDisplayAccountStatusPopup(false);
            statusTemplate && setAccountStatusTemplate(statusTemplate.popupTemplate);
            const statusWithoutPopup =
                !statusTemplate && accountStatusWithoutPopupMessages.find(item => selectedAccount?.accountStatus?.nameKey === item.status);
            let userEventMessage;
            if (statusTemplate) {
                userEventMessage = statusTemplate?.logEvent.screen;
            } else {
                userEventMessage = statusWithoutPopup?.logEvent.screen;
            }
            userEventMessage &&
                logUserEvent({
                    ...userEventMessage,
                    uuid: v4()
                });
        }, [RecordVWOAttributes, selectedAccount]);

        const showHVcard = isHomeValueEligible && !hasPropertyValueError;

        useEffect(() => {
            if (!isMount.current && showHVcard) {
                const data = {
                    action: 'HVTileDisplayed',
                    type: 'HVTileDisplayed',
                    description: 'Home Value Tile is displayed'
                };
                logUserEvent(data);
                isMount.current = true;
            }
        }, [showHVcard]);

        const apiEvents = useCallback((event: EventType) => {
            logApiEvent({
                apiName: event.apiName,
                action: event.action,
                correlationId: event.correlationId,
                timestamp: event.timestamp,
                loanNumber: event.loanNumber,
                description: event.description,
                type: 'Natural Disaster ACP widget'
            });
        }, []);
        const userEvents = useCallback((event: EventType) => {
            logUserEvent({
                type: 'Natural Disaster ACP widget',
                action: event.action,
                loanNumber: selectedLoanNumber?.identifier,
                description: event.description
            });
        }, []);

        const requestForbearance = useRequestForbearanceMutation({
            forbearanceApiParams: {
                baseAWSUrl: config.env('natDisasterAWSUrl') + `/${selectedLoanNumber?.identifier}/natural-disaster/`,
                xApiKey: config.env('xApiKey')
            },
            onComplete: async () => {
                await queryClient.invalidateQueries(['widgets']);
            },
            logApiEvent: apiEvents,
            logUserEvent: userEvents,
            type: 'Natural Disaster',
            accessToken: auth.getOkta().getAccessToken(),
            currentLoan: selectedLoanNumber
        });
        const handleOfferDeepLink = (deeplink: string, details: any, product: string) => {
            if (deeplink === 'od') {
                setDisclosures(
                    get(
                        OfferDisclosuresConstants,
                        [get(details, ['product_Campaign'], 'DEFAULT').toUpperCase()],
                        OfferDisclosuresConstants.DEFAULT
                    )
                );
            } else if (deeplink === 'fullapp') {
                if (details && get(flags, [details.product_Campaign], false)) {
                    navigate(`fullapp/${selectedLoanNumber.identifier}`, {
                        state: { offer: details, offerType: deeplink, product: product, loanNumber: selectedLoanNumber.identifier },
                        preventScrollReset: false
                    });
                } else {
                    navigate(`${details.product_Campaign}/${selectedLoanNumber.identifier}`, {
                        state: { ...details, offerType: product, loanNumber: selectedLoanNumber.identifier, leadOriginationSource: 'Tile' }
                    });
                }
            } else {
                navigate(`${deeplink}/${selectedLoanNumber.identifier}`, {
                    state: { ...details, offerType: product, loanNumber: selectedLoanNumber.identifier, leadOriginationSource: 'Tile' }
                });
            }
        };

        const onTaxDocumentDownload = () => {
            dispatch(fetchStatementAsync.request(loanNumber, '2278'));
        };

        const handleOnEscrow = () => {
            navigate(`/escrow-analysis/${selectedLoanNumber.identifier}`, { replace: false });
        };

        const onDeepLinkUserEvent = useCallback((event: any, offerType: any, details: any) => {
            logUserEvent({
                ...event,
                loanNumber,
                type: 'CommonOffer Wrapper Component',
                productCode: offerType,
                description: `${event?.action} - ${offerType}`,
                loanType: get(details, ['loanType'], '')
            });
            const vwoEvent = allowedVWOActionsMapping[`${event?.action}` || null];
            vwoEvent ? RecordVWOEvent(vwoEvent, { product: offerType }) : null;
        }, []);

        useEffect(() => {
            setShowNDModal(natStateModal);
        }, [natStateModal]);

        const natDisasterResponse = (
            response: { open: boolean; borrowerEffected: boolean },
            showMethod: (status: boolean) => void,
            navigate: (dest: string, options?: NavigateOptions) => void,
            loanNumber: string
        ): void => {
            showMethod(response.open);
            if (response.borrowerEffected) {
                navigate('natural-disaster', { replace: true });
            } else {
                handleNotImpacted(loanNumber);
            }
            triggerActivityEvent();
        };
        const triggerActivityEvent = () => {
            const url = config.env('activityUpdateUrl');
            activityEventUpdate(
                [
                    {
                        loanNumber: loanNumber,
                        activityType: 'Disaster',
                        activityResult: 'SHOWN'
                    }
                ],
                url
            );
        };
        const handleNotImpacted = (loanNumber: string) => {
            const request = {
                borrowerImpacted: 'N',
                determinationMethod: 'BORROWER NOTIFICATION',
                label: 'No',
                propertyImpactDetermination: 'N',
                workplaceImpactDetermination: 'N'
            };
            requestForbearance.mutate(request, {
                onSuccess: () => {
                    dispatch(clearLoanNDStatus(loanNumber));
                    navigate('disaster-assistance', { replace: true });
                },
                onError: () => {
                    enqueueSnackbar(NaturalDisasterConstants.FAILURE_FORBEARANCE_MESSAGE, snackProps);
                    dispatch(clearLoanNDStatus(loanNumber));
                    navigate('disaster-assistance', { replace: true });
                }
            });
        };

        React.useEffect(() => {
            if (!isEmpty(siteMessageRequest)) {
                dispatch(fetchAlertAsync.request(siteMessageRequest));
            }
        }, [siteMessageRequest]);

        const closeModal = () => {
            setDisclosures(false);
        };

        useEffect(() => {
            if (isEmpty(templates) || isEmpty(loanNumber)) return;
            templates.map(({ offerType }: any) => {
                logUserEvent({
                    action: 'Offer Displayed',
                    description: `${offerType} - Offer displayed`,
                    type: 'CommonOffer Wrapper Component',
                    loanNumber
                });
                RecordVWOEvent('offerDisplayed', { product: offerType });
            });
        }, [loanNumber, templates]);

        const isPaymentPastDue = useMemo(() => {
            const today = moment();
            let paymentDate = null;

            if (selectedAccount?.nextPaymentDate) {
                paymentDate = moment(selectedAccount.nextPaymentDate).format('YYYY-MM-DD');
            }

            if (paymentDate) {
                return today.isAfter(paymentDate);
            }

            return false;
        }, [selectedAccount]);

        // if (!templates) return null;
        if (isMobile)
            return (
                <Container maxWidth="xl">
                    <TurboTaxModal isOpen={isOpen} onClose={() => toggle()} />
                    <DialogBase
                        key="offer_terms"
                        open={!isEmpty(disclosures)}
                        title="Offer Terms & Conditions"
                        renderDialogContent={() => <TypographyBase sx={{ whiteSpace: 'pre-wrap' }}>{disclosures}</TypographyBase>}
                        handleClose={closeModal}
                    />
                    {!showNDModal && isHomeValueCOmplete && <NBPoffer />}
                    {displayAccountStatusPopup && (
                        <AccountStatusPopup
                            open={displayAccountStatusPopup}
                            handleClose={setDisplayAccountStatusPopup}
                            accountStatusTemplate={accountStatusTemplate}
                        />
                    )}
                    <NaturalDisasterModal
                        open={showNDModal}
                        handleClose={response => natDisasterResponse(response, setShowNDModal, navigate, selectedLoanNumber.identifier)}
                        homeAddress={currentAddress}
                    />
                    <Onboarding path="/" />
                    <StackBase gap={theme.spacing(2)} my={theme.spacing(2)}>
                        <SiteMessages handleLinkClick={setDisplayAccountStatusPopup} />
                        {!isPaymentPastDue ? <NewLoanInProgressTiles /> : null}
                        {isEmpty(originations) && teaserOffer ? (
                            <TeaserOffer
                                offerTemplate={teaserOffer.template}
                                logUserEvent={(event: any) => onDeepLinkUserEvent(event, teaserOffer.offerType, teaserOffer.offerDetails)}
                                onDeepLink={(deeplink: string) =>
                                    handleOfferDeepLink(deeplink, teaserOffer.offerDetails, teaserOffer.offerType)
                                }
                                key={`teaser_template`}
                            ></TeaserOffer>
                        ) : null}
                        {isLoading ? <PaymentCardSkeleton /> : <PaymentCard selectedAccount={selectedAccount} />}
                        {isPaymentPastDue ? <NewLoanInProgressTiles /> : null}
                        {!isEmpty(originations) && teaserOffer ? (
                            <TeaserOffer
                                offerTemplate={teaserOffer.template}
                                logUserEvent={(event: any) => onDeepLinkUserEvent(event, teaserOffer.offerType, teaserOffer.offerDetails)}
                                onDeepLink={(deeplink: string) =>
                                    handleOfferDeepLink(deeplink, teaserOffer.offerDetails, teaserOffer.offerType)
                                }
                                key={`teaser_template`}
                            ></TeaserOffer>
                        ) : null}
                        {!templates
                            ? null
                            : templates
                                  .filter((template: any) => template.priorityNo !== get(teaserOffer, ['priorityNo'], ''))
                                  .map((item: any, idx: number) => {
                                      return (
                                          <TileContextProvider
                                              {...item.template}
                                              logUserEvent={event => onDeepLinkUserEvent(event, item.offerType, item.offerDetails)}
                                              onDeepLink={(deeplink: string) =>
                                                  handleOfferDeepLink(deeplink, item.offerDetails, item.offerType)
                                              }
                                              key={`${idx}_template`}
                                          >
                                              <Tile key={`${idx}_template_tile`} id={item.offerType} />
                                          </TileContextProvider>
                                      );
                                  })}
                        {!isEscrowEligible ? null : (
                            <TileContextProvider
                                {...EscrowTile}
                                onDeepLink={handleOnEscrow}
                                logUserEvent={event => console.log(event)}
                                key={`escrow_tile_template_provider`}
                            >
                                <Tile key={`escrow_template_tile`} />
                            </TileContextProvider>
                        )}
                        {!is1098Eligible ? null : (
                            <TaxDocumentWidget
                                loanNumber={selectedAccount?.accountNumber}
                                themeType=""
                                id={CodingConstants.taxDocumentWidget}
                                handleDocument={onTaxDocumentDownload}
                            />
                        )}
                        {!isPaperLessEligible ? null : <PaperlessEnrollment selectedAccount={selectedAccount} themeType="" />}
                        {!showHVcard ? null : <HomeValueContainer loanNumber={loanNumber} handleOfferDeepLink={handleOfferDeepLink} />}
                    </StackBase>
                </Container>
            );
        return (
            <BoxBase maxWidth="xl" sx={{ padding: { xs: '1rem', sm: '2rem' }, width: '100%', margin: '0 auto' }}>
                <TurboTaxModal isOpen={isOpen} onClose={() => toggle()} />
                <DialogBase
                    key="offer_terms"
                    open={!isEmpty(disclosures)}
                    title="Offer Terms & Conditions"
                    renderDialogContent={() => <TypographyBase sx={{ whiteSpace: 'pre-wrap' }}>{disclosures}</TypographyBase>}
                    handleClose={closeModal}
                />
                {!showNDModal && isHomeValueCOmplete && <NBPoffer />}
                {displayAccountStatusPopup && (
                    <AccountStatusPopup
                        open={displayAccountStatusPopup}
                        handleClose={setDisplayAccountStatusPopup}
                        accountStatusTemplate={accountStatusTemplate}
                    />
                )}
                <NaturalDisasterModal
                    open={showNDModal}
                    handleClose={response => natDisasterResponse(response, setShowNDModal, navigate, selectedLoanNumber.identifier)}
                    homeAddress={currentAddress}
                />
                <Onboarding path="/" />
                <SiteMessages handleLinkClick={setDisplayAccountStatusPopup} />
                <StackBase gap={theme.spacing(4)} direction="row" justifyContent="space-between">
                    <Columned>
                        {!isPaymentPastDue ? <NewLoanInProgressTiles /> : null}
                        {isLoading ? <PaymentCardSkeleton /> : <PaymentCard selectedAccount={selectedAccount} />}
                        {!isEscrowEligible ? null : (
                            <TileContextProvider
                                {...EscrowTile}
                                onDeepLink={handleOnEscrow}
                                logUserEvent={event => console.log(event)}
                                key={`escrow_tile_template_provider`}
                            >
                                <Tile key={`escrow_template_tile`} />
                            </TileContextProvider>
                        )}
                        {!is1098Eligible ? null : (
                            <TaxDocumentWidget
                                loanNumber={selectedAccount?.accountNumber}
                                themeType=""
                                id={CodingConstants.taxDocumentWidget}
                                handleDocument={onTaxDocumentDownload}
                            />
                        )}
                        {!isPaperLessEligible ? null : <PaperlessEnrollment selectedAccount={selectedAccount} themeType="" />}
                        {/*)}*/}
                        {!showHVcard ? null : <HomeValueContainer loanNumber={loanNumber} handleOfferDeepLink={handleOfferDeepLink} />}
                    </Columned>
                    <Columned>
                        {isPaymentPastDue ? <NewLoanInProgressTiles /> : null}
                        {!templates
                            ? null
                            : templates.map(
                                  (
                                      { template, offerDetails, offerType }: { template: any; offerDetails: any; offerType: string },
                                      idx: number
                                  ) => (
                                      <TileContextProvider
                                          {...template}
                                          logUserEvent={event => onDeepLinkUserEvent(event, offerType, offerDetails)}
                                          onDeepLink={(deeplink: string) => handleOfferDeepLink(deeplink, offerDetails, offerType)}
                                          key={`${idx}_template`}
                                      >
                                          <Tile key={`${idx}_template_tile`} id={offerType} />
                                      </TileContextProvider>
                                  )
                              )}
                    </Columned>
                </StackBase>
            </BoxBase>
        );
    },
    { showFooterAds: true }
);

ACPTiles.displayName = 'NewDashboard';
